/**
 * OpenTelemetry Tracer Configuration
 * 
 * This module initializes OpenTelemetry tracing only when REACT_APP_OTLP_API_URL is configured.
 * If the URL is not present or invalid, OpenTelemetry will be disabled gracefully.
 * 
 * To enable OpenTelemetry:
 * - Set REACT_APP_OTLP_API_URL environment variable to your OTLP collector endpoint
 * - Example: REACT_APP_OTLP_API_URL=http://localhost:4318/v1/traces
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';

const { getOtlpApiUrl } = require('./util');

/**
 * Validates if the OTLP API URL is properly configured
 * @param {string|undefined|null} url - The OTLP API URL to validate
 * @returns {boolean} - True if the URL is valid, false otherwise
 */
const isValidOtlpUrl = (url) => {
  // Check if URL exists, is a string, and is not empty or whitespace-only
  return url && typeof url === 'string' && url.trim().length > 0;
};

/**
 * Initialize OpenTelemetry tracing if configured
 * This is a configurable feature that only activates when REACT_APP_OTLP_API_URL is set
 */
const initializeOpenTelemetry = () => {
  const otlpApiUrl = getOtlpApiUrl();

  // Feature flag: Only initialize if OTLP API URL is configured
  if (!isValidOtlpUrl(otlpApiUrl)) {
    console.warn(
      'OpenTelemetry: REACT_APP_OTLP_API_URL is not configured or is invalid. ' +
      'OpenTelemetry tracing is disabled. To enable, set REACT_APP_OTLP_API_URL environment variable.'
    );
    return; // Exit early - feature is disabled
  }

  // URL is configured - proceed with initialization
  try {
    // Create the tracer provider with resource metadata
    const provider = new WebTracerProvider({
      resource: new Resource({
        [SemanticResourceAttributes.SERVICE_NAME]: 'developer2-0-ui',
        [SemanticResourceAttributes.SERVICE_VERSION]: '21.9.0',
      }),
    });

    // Set up OTLP trace exporter with the configured URL
    const exporter = new OTLPTraceExporter({
      url: otlpApiUrl.trim(),
    });

    // Add span processor to the provider
    provider.addSpanProcessor(new BatchSpanProcessor(exporter));

    // Register the provider to enable instrumentation
    provider.register();

    // Register instrumentations for automatic tracing
    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation(),
        new XMLHttpRequestInstrumentation(),
      ],
    });
  } catch (error) {
    console.error('OpenTelemetry: Failed to initialize:', error);
    // Fail gracefully - app continues without OpenTelemetry
  }
};

// Initialize OpenTelemetry only if configured
initializeOpenTelemetry();

